import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  CalendarDays,
  ClipboardList,
  FileEdit,
  Globe2,
  History,
  Lightbulb,
  Link2,
  Megaphone,
  Network,
  Rocket,
  Search,
  Sparkles,
  Users,
} from "lucide-react";
import useBrandDetailsApi from "../../api/useBrandDetailsApi";
import useCurrentPlan from "../../hooks/use-current-plan";
import type { BrandDetails as BrandDetailsType } from "../../types/add-brand-types";
import type { BillingPlanFeatures } from "../../types/billing";
import { AppRoute } from "../../types/routes";
import Spinner from "../uiComponents/Spinner";
import "./brand-features.css";

type BrandTab = "strategy" | "drafts" | "weekly-plan" | "calendar" | "history" | "connections" | "growth";
type GrowthTab = "monitor" | "seo" | "leads";
type FeatureStatus = "available" | "beta" | "paid" | "checking";

interface FeatureItem {
  title: string;
  description: string;
  category: "setup" | "planning" | "creation" | "growth" | "tracking";
  icon: React.ReactNode;
  tab: BrandTab;
  growthTab?: GrowthTab;
  featureKey?: keyof BillingPlanFeatures;
  cta: string;
}

const CORE_FEATURES: FeatureItem[] = [
  {
    title: "Client Brand Context",
    description:
      "Store each brand's tone, audience, competitors, positioning, and brand URL in a dedicated workspace.",
    category: "setup",
    icon: <BookOpen size={22} />,
    tab: "strategy",
    cta: "Open Brand Knowledge",
  },
  {
    title: "Website and PDF Knowledge",
    description:
      "Add website context and uploaded documents so future drafts can use the client's source material.",
    category: "setup",
    icon: <Globe2 size={22} />,
    tab: "strategy",
    cta: "Manage sources",
  },
  {
    title: "Creator Studio",
    description:
      "Generate three draft variations from one prompt, then choose the strongest version to customize.",
    category: "creation",
    icon: <Sparkles size={22} />,
    tab: "drafts",
    cta: "Create content",
  },
  {
    title: "Platform-Specific Drafts",
    description:
      "Adapt copy, media, and scheduling details for supported social platforms before publishing.",
    category: "creation",
    icon: <Megaphone size={22} />,
    tab: "drafts",
    cta: "Open drafts",
  },
  {
    title: "Weekly Plan",
    description:
      "Generate a weekly list of social post ideas and one SEO draft task with planned dates.",
    category: "planning",
    icon: <ClipboardList size={22} />,
    tab: "weekly-plan",
    cta: "Open weekly plan",
  },
  {
    title: "Content Calendar and Scheduling",
    description:
      "Review planned, scheduled, published, and failed items in a calendar view for this brand.",
    category: "planning",
    icon: <CalendarDays size={22} />,
    tab: "calendar",
    featureKey: "scheduling",
    cta: "Open calendar",
  },
  {
    title: "SEO Drafts",
    description:
      "Use the Growth area to find SEO opportunities and generate article drafts for this brand.",
    category: "growth",
    icon: <Search size={22} />,
    tab: "growth",
    growthTab: "seo",
    featureKey: "seo",
    cta: "Open SEO",
  },
  {
    title: "Social Signal Listening",
    description:
      "Track social signals for configured topics so the team can turn market activity into content ideas.",
    category: "growth",
    icon: <Rocket size={22} />,
    tab: "growth",
    growthTab: "monitor",
    featureKey: "socialSignalListening",
    cta: "Open signals",
  },
  {
    title: "Lead Suggestions",
    description:
      "Browse lead suggestions from the Growth area when a brand needs prospecting support.",
    category: "growth",
    icon: <Users size={22} />,
    tab: "growth",
    growthTab: "leads",
    featureKey: "leads",
    cta: "Open leads",
  },
  {
    title: "Content History",
    description:
      "Review generated drafts, scheduled posts, published content, and failed publishing attempts.",
    category: "tracking",
    icon: <History size={22} />,
    tab: "history",
    cta: "Open history",
  },
  {
    title: "Published Post Analytics",
    description:
      "Open analytics from published history items when platform data is available for a post.",
    category: "tracking",
    icon: <BarChart3 size={22} />,
    tab: "history",
    cta: "View history",
  },
  {
    title: "Connected Platforms",
    description:
      "Connect supported social accounts for this brand and keep publishing access scoped to the workspace.",
    category: "setup",
    icon: <Network size={22} />,
    tab: "connections",
    cta: "Open connections",
  },
];

const CATEGORY_LABELS: Record<FeatureItem["category"], string> = {
  setup: "Brand setup",
  planning: "Planning",
  creation: "Content creation",
  growth: "Growth",
  tracking: "Tracking",
};

const STATUS_LABELS: Record<FeatureStatus, string> = {
  available: "Available",
  beta: "Beta unlocked",
  paid: "Paid plan",
  checking: "Checking",
};

function getFeatureStatus(
  item: FeatureItem,
  plan: ReturnType<typeof useCurrentPlan>["plan"],
  isPlanLoading: boolean,
): FeatureStatus {
  if (!item.featureKey) {
    return "available";
  }

  if (isPlanLoading || !plan) {
    return "checking";
  }

  if (plan.features[item.featureKey]) {
    return plan.betaEnabled ? "beta" : "available";
  }

  return "paid";
}

export default function BrandFeatures() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { getBrandDetails } = useBrandDetailsApi();
  const { plan, loading: loadingPlan } = useCurrentPlan(brandId);
  const [brand, setBrand] = useState<BrandDetailsType | null>(null);
  const [loadingBrand, setLoadingBrand] = useState(Boolean(brandId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId) {
      setError("Missing brand id.");
      setLoadingBrand(false);
      return;
    }

    let isMounted = true;
    setLoadingBrand(true);
    setError(null);

    getBrandDetails(brandId)
      .then((response) => {
        if (!isMounted) {
          return;
        }
        if (!response?.data) {
          setError("Unable to load brand details.");
          return;
        }
        setBrand(response.data);
      })
      .catch((err) => {
        console.error("Failed to load brand features page", err);
        if (isMounted) {
          setError("Unable to load brand details.");
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoadingBrand(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [brandId, getBrandDetails]);

  const groupedFeatures = useMemo(() => {
    return CORE_FEATURES.reduce<Record<FeatureItem["category"], FeatureItem[]>>(
      (acc, item) => {
        acc[item.category].push(item);
        return acc;
      },
      {
        setup: [],
        planning: [],
        creation: [],
        growth: [],
        tracking: [],
      },
    );
  }, []);

  const planLabel = loadingPlan ? "..." : plan?.planTier ?? "free";
  const brandLimitLabel = loadingPlan
    ? "..."
    : plan?.limits.brands == null
      ? "Unlimited"
      : plan.limits.brands;

  const openFeature = useCallback(
    (item: FeatureItem) => {
      if (!brandId) {
        return;
      }
      const route = AppRoute.BRAND_DETAILS.replace(":id", brandId);
      navigate(route, {
        state: {
          activeTab: item.tab,
          ...(item.growthTab ? { growthTab: item.growthTab } : {}),
        },
      });
    },
    [brandId, navigate],
  );

  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(AppRoute.HOME, { replace: true });
  }, [navigate]);

  if (loadingBrand) {
    return (
      <div className="features-page features-page--centered">
        <Spinner size="large" />
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="features-page features-page--centered">
        <div className="features-error">
          <h1>Features are unavailable</h1>
          <p>{error ?? "Unable to load brand details."}</p>
          <button type="button" onClick={() => navigate(AppRoute.HOME)}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="features-page">
      <header className="features-header">
        <button type="button" className="features-back" onClick={goBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="features-brand-card">
          <div className="features-brand-avatar">{brand.brand_name.charAt(0)}</div>
          <div>
            <p className="features-eyebrow">Features</p>
            <h1>{brand.brand_name}</h1>
            {brand.marketing_strategy?.brand_url && (
              <a
                href={brand.marketing_strategy.brand_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {brand.marketing_strategy.brand_url}
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="features-content">
        <section className="features-summary">
          <div>
            <p className="features-eyebrow">What is included</p>
            <h2>Use this page to find what Impret can do for this brand.</h2>
          </div>
          <div className="features-summary-grid">
            <div>
              <span>{planLabel}</span>
              <p>Current plan</p>
            </div>
            <div>
              <span>{brandLimitLabel}</span>
              <p>Brand limit</p>
            </div>
            <div>
              <span>{brand.connected_platforms?.length ?? 0}</span>
              <p>Connected platforms</p>
            </div>
          </div>
        </section>

        {Object.entries(groupedFeatures).map(([category, items]) => (
          <section className="features-section" key={category}>
            <h3>{CATEGORY_LABELS[category as FeatureItem["category"]]}</h3>
            <div className="features-grid">
              {items.map((item) => {
                const status = getFeatureStatus(item, plan, loadingPlan);
                return (
                  <article className="feature-card" key={item.title}>
                    <div className="feature-card-top">
                      <div className="feature-icon">{item.icon}</div>
                      <span className={`feature-status feature-status--${status}`}>
                        {STATUS_LABELS[status]}
                      </span>
                    </div>
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                    <button type="button" onClick={() => openFeature(item)}>
                      {status === "paid" ? "View upgrade" : item.cta}
                    </button>
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        <section className="features-note">
          <Lightbulb size={20} />
          <div>
            <h3>Feature statuses are based on your current plan.</h3>
            <p>
              Paid features open the existing product area, where Impret shows
              the upgrade path when that capability is not enabled.
            </p>
          </div>
        </section>

        <section className="features-actions">
          <button
            type="button"
            className="features-primary-action"
            onClick={() =>
              navigate(AppRoute.BRAND_DETAILS.replace(":id", brand.id), {
                state: { activeTab: "weekly-plan" },
              })
            }
          >
            <ClipboardList size={18} />
            Open weekly plan
          </button>
          <button
            type="button"
            className="features-secondary-action"
            onClick={() =>
              navigate(AppRoute.BRAND_DETAILS.replace(":id", brand.id), {
                state: { activeTab: "drafts" },
              })
            }
          >
            <FileEdit size={18} />
            Create content
          </button>
          <button
            type="button"
            className="features-secondary-action"
            onClick={() =>
              navigate(AppRoute.BRAND_DETAILS.replace(":id", brand.id), {
                state: { activeTab: "connections" },
              })
            }
          >
            <Link2 size={18} />
            Manage connections
          </button>
        </section>
      </main>
    </div>
  );
}
